import { action, makeObservable, runInAction } from 'mobx';
import { GoodsStore } from './store';
import { getUserAddressList, readGoodsDetail, readGoodsList, submitGoodsExchange, submitLotteryGoodsDraw } from '@server';

const parseDateTime = (value) => {
    if (!value) return NaN;
    return new Date(String(value).replace(/-/g, '/')).getTime();
};

export class GoodsAction extends GoodsStore {
    constructor(props = {}) {
        super(props);
        makeObservable(this);
    }

    @action
    setGoodsData(data) {
        this.goodsData = data;
    }

    @action
    clearGoodsData() {
        this.goodsData = null;
        this.goodsDetailLoading = false;
        this.goodsDetailError = null;
        this.goodsDetailErrorCode = null;
    }

    @action
    async getGoodsDetail(params = {}) {
        this.goodsDetailLoading = true;
        this.goodsDetailError = null;
        this.goodsDetailErrorCode = null;
        this.goodsData = null;

        const res = await readGoodsDetail(params);

        runInAction(() => {
            if (res.code === 0) {
                this.goodsData = res.data;
            } else {
                this.goodsDetailError = res.msg || '商品详情加载失败';
                this.goodsDetailErrorCode = res.code;
            }
            this.goodsDetailLoading = false;
        });

        return res;
    }

    @action
    async getGoodsList(params = {}) {
        const page = Math.max(Number(params.page) || 1, 1);
        const perPage = Math.max(Number(params.perPage) || 10, 1);
        // 商品详情推荐列表只读取可售商品，所有分页和分类请求统一携带该筛选条件。
        const requestParams = {
            ...params,
            'filter[saleStatus]': 2,
            page,
            perPage,
        };
        this.goodsListLoading = true;
        this.goodsListError = null;
        // 第一页切换分类时重置列表，后续页请求保留已有数据并追加。
        if (page === 1) this.goodsList = [];

        const res = await readGoodsList(requestParams);

        runInAction(() => {
            if (res.code === 0) {
                const responseData = res.data;
                const rawList = Array.isArray(responseData)
                    ? responseData
                    : responseData?.list
                        || responseData?.pageData
                        || (Array.isArray(responseData?.data) ? responseData.data : []);
                const serverTime = Number(responseData?.serverAt) > 0
                    ? Number(responseData.serverAt) * 1000
                    : Date.now();
                const nextList = rawList.map(item => {
                    const listAt = parseDateTime(item.list_at);
                    const remainingTime = Number.isFinite(listAt)
                        ? Math.max(listAt - serverTime, 0)
                        : 0;

                    return {
                        ...item,
                        remainingTime,
                        countdownTarget: Date.now() + remainingTime,
                        saleStarted: remainingTime <= 0,
                    };
                });
                this.goodsList = page > 1
                    ? [...this.goodsList, ...nextList]
                    : nextList;
                this.goodsListTotal = Array.isArray(responseData)
                    ? responseData.length
                    : Number(responseData?.total) || this.goodsList.length;
                this.goodsListServerAt = responseData?.serverAt || null;
            } else {
                if (page === 1) {
                    this.goodsList = [];
                    this.goodsListTotal = 0;
                }
                this.goodsListServerAt = null;
                this.goodsListError = res.msg || '商品列表加载失败';
            }
            this.goodsListLoading = false;
        });

        return res;
    }

    @action
    startGoodsSale(goodsCode) {
        this.goodsList = this.goodsList.map(item => (
            String(item.code) === String(goodsCode)
                ? { ...item, remainingTime: 0, saleStarted: true }
                : item
        ));
    }

    @action
    selectGoodsAddress(address) {
        this.defaultAddress = address || null;
    }

    @action
    async exchangeGoods(params = {}) {
        if (this.goodsExchangeLoading) return null;
        this.goodsExchangeLoading = true;
        this.goodsExchangeError = null;

        try {
            const res = await submitGoodsExchange(params);

            runInAction(() => {
                if (res.code === 0 && this.goodsData) {
                    const remainingQuota = Number(this.goodsData.remaining_quota);
                    this.goodsData = {
                        ...this.goodsData,
                        ...(Number.isFinite(remainingQuota)
                            ? { remaining_quota: Math.max(remainingQuota - 1, 0) }
                            : {}),
                    };
                } else if (res.code !== 0) {
                    this.goodsExchangeError = res.msg || '兑换失败';
                }
                this.goodsExchangeLoading = false;
            });

            return res;
        } catch (error) {
            runInAction(() => {
                this.goodsExchangeError = error?.message || '兑换失败';
                this.goodsExchangeLoading = false;
            });
            return { code: -1, msg: this.goodsExchangeError };
        }
    }

    @action
    async drawLotteryGoods(params = {}) {
        if (this.goodsLotteryLoading) return null;
        this.goodsLotteryLoading = true;
        this.goodsLotteryError = null;

        try {
            const res = await submitLotteryGoodsDraw(params);
            let stockResponse = null;

            if (Number(res?.code) === 0 && params?.code !== undefined && params?.code !== null) {
                try {
                    // 抽奖成功后只读取详情接口的库存，不改动详情 loading 和其他字段。
                    stockResponse = await readGoodsDetail({ code: params.code });
                } catch (error) {
                    // 库存同步失败不影响抽奖结果弹框，保留当前库存等待下次详情请求校准。
                    stockResponse = null;
                }
            }

            runInAction(() => {
                if (Number(res?.code) === 0 && this.goodsData) {
                    // 抽奖接口不刷新详情，只同步详情接口返回的库存字段。
                    const responseData = stockResponse?.data?.data
                        || stockResponse?.data
                        || res?.data?.data
                        || res?.data
                        || {};
                    const responseStock = responseData?.goods_stock
                        ?? responseData?.goodsStock
                        ?? responseData?.stock;
                    const isSameGoods = params?.code === undefined
                        || params?.code === null
                        || String(this.goodsData.code) === String(params.code);
                    if (isSameGoods && responseStock !== undefined && responseStock !== null) {
                        this.goodsData = {
                            ...this.goodsData,
                            goods_stock: responseStock,
                        };
                    }
                } else if (Number(res?.code) === -14006 && this.goodsData) {
                    // 后端明确返回售罄时，将本地库存置零，立即切换为已结束状态。
                    this.goodsData = {
                        ...this.goodsData,
                        goods_stock: 0,
                    };
                }

                if (Number(res?.code) !== 0) {
                    this.goodsLotteryError = res.msg || '抽奖失败';
                }
                this.goodsLotteryLoading = false;
            });

            return res;
        } catch (error) {
            runInAction(() => {
                this.goodsLotteryError = error?.message || '抽奖失败';
                this.goodsLotteryLoading = false;
            });
            return { code: -1, msg: this.goodsLotteryError };
        }
    }

    @action
    async getGoodsAddressList(params = {}) {
        this.addressLoading = true;
        this.addressLoaded = false;
        this.addressError = null;

        try {
            const res = await getUserAddressList(params);

            runInAction(() => {
                if (res.code === 0) {
                    const responseData = res.data;
                    const addressList = Array.isArray(responseData)
                        ? responseData
                        : responseData?.list || responseData?.pageData || responseData?.data || [];
                    this.addressList = addressList;
                    this.defaultAddress = addressList.find(item => (
                        Number(item.is_default ?? item.isDefault ?? item.default) === 1
                    )) || null;
                    this.addressLoaded = true;
                } else {
                    this.addressList = [];
                    this.defaultAddress = null;
                    this.addressError = res.msg || '收货地址加载失败';
                }
                this.addressLoading = false;
            });

            return res;
        } catch (error) {
            runInAction(() => {
                this.addressList = [];
                this.defaultAddress = null;
                this.addressError = error?.message || '收货地址加载失败';
                this.addressLoading = false;
            });

            return { code: -1, msg: this.addressError };
        }
    }
}

const goodsAction = new GoodsAction();
export default goodsAction;
